import { supabaseService } from './supabaseClient.js';
import { smtpEmailService } from './smtpEmailService.js';
import { API_CONFIG, APP_CONFIG, ERROR_MESSAGES } from '../utils/constants.js';

/**
 * Email service class for handling email notifications
 */
export class EmailService {
  constructor() {
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second base delay
  }

  /**
   * Send user confirmation email
   * @param {string} userEmail - User's email address
   * @param {string} userName - User's name
   * @param {string} walletAddress - User's wallet address
   * @param {string} entryId - Whitelist entry ID for logging
   * @returns {Promise<Object>} Result object with success status
   */
  async sendUserConfirmation(userEmail, userName, walletAddress, entryId) {
    const emailType = 'user_confirmation';
    
    try {
      console.log('📧 Preparing user confirmation email for:', userEmail);
      
      // Log email attempt as pending
      await supabaseService.logEmailAttempt({
        whitelistEntryId: entryId,
        emailType,
        recipientEmail: userEmail,
        status: 'pending'
      });

      // Generate email content
      const emailContent = this._generateUserConfirmationEmail(userName, walletAddress);
      console.log('📧 Generated user confirmation email content, sending...');
      
      // Send email with retry logic
      const result = await this._sendEmailWithRetry({
        to: userEmail,
        subject: '🚀 Welcome to the $PGC Whitelist - Confirmation Required',
        html: emailContent.html,
        text: emailContent.text
      });

      console.log('📧 User confirmation email send result:', result);

      // Log successful email
      await supabaseService.logEmailAttempt({
        whitelistEntryId: entryId,
        emailType,
        recipientEmail: userEmail,
        status: 'sent',
        emailProviderId: result.messageId
      });

      return {
        success: true,
        message: 'Confirmation email sent successfully'
      };

    } catch (error) {
      console.error('❌ Error sending user confirmation email:', error);

      // Log failed email attempt
      try {
        await supabaseService.logEmailAttempt({
          whitelistEntryId: entryId,
          emailType,
          recipientEmail: userEmail,
          status: 'failed',
          errorMessage: error.message
        });
      } catch (logError) {
        console.error('❌ Error logging failed email attempt:', logError);
      }

      return {
        success: false,
        error: 'EMAIL_SEND_ERROR',
        message: ERROR_MESSAGES.EMAIL_SEND_ERROR,
        details: {
          timestamp: new Date().toISOString(),
          recipient: userEmail,
          emailType: 'user_confirmation',
          error: error.message,
          code: error.code || 'UNKNOWN_ERROR',
          // Don't expose sensitive information like passwords or API keys
          smtpHost: process.env.VITE_SMTP_HOST || process.env.SMTP_HOST || 'smtp.gmail.com',
          smtpPort: process.env.VITE_SMTP_PORT || process.env.SMTP_PORT || '587'
        }
      };
    }
  }

  /**
   * Send admin notification email
   * @param {Object} entryData - Whitelist entry data
   * @returns {Promise<Object>} Result object with success status
   */
  async sendAdminNotification(entryData) {
    const emailType = 'admin_notification';
    const adminEmail = API_CONFIG.adminEmail;
    
    try {
      // Log email attempt as pending
      await supabaseService.logEmailAttempt({
        whitelistEntryId: entryData.id,
        emailType,
        recipientEmail: adminEmail,
        status: 'pending'
      });

      // Generate email content
      const emailContent = this._generateAdminNotificationEmail(entryData);
      
      // Send email with retry logic
      const result = await this._sendEmailWithRetry({
        to: adminEmail,
        subject: '🔔 New $PGC Whitelist Registration',
        html: emailContent.html,
        text: emailContent.text
      });

      // Log successful email
      await supabaseService.logEmailAttempt({
        whitelistEntryId: entryData.id,
        emailType,
        recipientEmail: adminEmail,
        status: 'sent',
        emailProviderId: result.messageId
      });

      return {
        success: true,
        message: 'Admin notification sent successfully'
      };

    } catch (error) {
      console.error('Error sending admin notification email:', error);

      // Log failed email attempt
      try {
        await supabaseService.logEmailAttempt({
          whitelistEntryId: entryData.id,
          emailType,
          recipientEmail: adminEmail,
          status: 'failed',
          errorMessage: error.message
        });
      } catch (logError) {
        console.error('Error logging failed admin email attempt:', logError);
      }

      return {
        success: false,
        error: 'EMAIL_SEND_ERROR',
        message: 'Failed to send admin notification'
      };
    }
  }

  /**
   * Send both user confirmation and admin notification
   * @param {Object} entryData - Complete whitelist entry data
   * @returns {Promise<Object>} Combined result object
   */
  async sendAllNotifications(entryData) {
    console.log('📧 Starting email notifications for entry:', entryData.id);
    
    const results = {
      userEmail: { success: false },
      adminEmail: { success: false }
    };

    try {
      // Send user confirmation email
      console.log('📧 Sending user confirmation email to:', entryData.email);
      results.userEmail = await this.sendUserConfirmation(
        entryData.email,
        entryData.name,
        entryData.walletAddress,
        entryData.id
      );
      console.log('📧 User confirmation email result:', results.userEmail);

      // Send admin notification email
      console.log('📧 Sending admin notification email to:', API_CONFIG.adminEmail);
      results.adminEmail = await this.sendAdminNotification(entryData);
      console.log('📧 Admin notification email result:', results.adminEmail);

      // Update email sent status in database if user email was successful
      if (results.userEmail.success) {
        console.log('📧 Updating email sent status for entry:', entryData.id);
        await supabaseService.updateEmailSentStatus(entryData.id, true);
      }

      const allSuccessful = results.userEmail.success && results.adminEmail.success;
      const anySuccessful = results.userEmail.success || results.adminEmail.success;

      console.log('📧 Email notifications completed:', { allSuccessful, anySuccessful, results });

      return {
        success: allSuccessful,
        partialSuccess: anySuccessful && !allSuccessful,
        results,
        message: allSuccessful 
          ? 'All email notifications sent successfully'
          : anySuccessful 
            ? 'Some email notifications were sent successfully'
            : 'Failed to send email notifications'
      };

    } catch (error) {
      console.error('❌ Error sending email notifications:', error);
      return {
        success: false,
        partialSuccess: false,
        results,
        error: 'EMAIL_SEND_ERROR',
        message: ERROR_MESSAGES.EMAIL_SEND_ERROR
      };
    }
  }

  /**
   * Send email with retry logic and fallback to SMTP
   * @private
   * @param {Object} emailData - Email data
   * @returns {Promise<Object>} Send result
   */
  async _sendEmailWithRetry(emailData) {
    console.log('📧 Attempting to send email with retry logic');
    
    let lastError;

    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        console.log(`📧 Send attempt ${attempt}/${this.maxRetries}`);
        
        // Try Supabase Edge Function first
        const supabaseResult = await this._sendViaSupabase(emailData);
        
        if (supabaseResult.success) {
          console.log('✅ Email sent successfully via Supabase');
          return supabaseResult;
        }
        
        // If Supabase fails, try SMTP as fallback
        console.log('📧 Supabase failed, trying SMTP fallback...');
        lastError = new Error(supabaseResult.error || 'Supabase function unavailable');
        
      } catch (error) {
        console.log('📧 Supabase attempt failed, will try SMTP fallback:', error.message);
        lastError = error;
      }

      // Try SMTP fallback
      try {
        console.log('📧 Trying SMTP fallback...');
        const smtpResult = await this._sendViaSmtp(emailData);
        
        if (smtpResult.success) {
          console.log('✅ Email sent successfully via SMTP');
          return smtpResult;
        }
        
        console.log('📧 SMTP attempt also failed:', smtpResult.error);
        lastError = new Error(smtpResult.error || 'SMTP fallback failed');
        
      } catch (smtpError) {
        console.log('📧 SMTP attempt failed with exception:', smtpError.message);
        lastError = smtpError;
      }

      if (attempt === this.maxRetries) {
        console.log(`📧 Reached max retries (${this.maxRetries}), giving up`);
        break;
      }

      // Exponential backoff
      const delay = this.retryDelay * Math.pow(2, attempt - 1);
      console.warn(`Email send failed (attempt ${attempt}/${this.maxRetries}), retrying in ${delay}ms:`, lastError);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    console.error('❌ All email sending attempts failed:', lastError);
    throw lastError;
  }

  /**
   * Send email via SMTP service
   * @private
   * @param {Object} emailData - Email data
   * @returns {Promise<Object>} Send result
   */
  async _sendViaSmtp(emailData) {
    try {
      console.log('📧 Sending email via SMTP service');
      
      // Validate SMTP service is available
      if (!smtpEmailService) {
        console.log('📧 SMTP service not available');
        return {
          success: false,
          error: 'SMTP_SERVICE_UNAVAILABLE'
        };
      }

      // Send email via SMTP
      console.log('📧 Calling SMTP service sendEmail method');
      const result = await smtpEmailService.sendEmail(emailData);
      
      console.log('📧 SMTP service result:', result);
      
      if (result.success) {
        return {
          success: true,
          messageId: result.messageId,
          response: result.response
        };
      } else {
        return {
          success: false,
          error: result.error || 'SMTP_SEND_FAILED',
          errorCode: result.errorCode
        };
      }

    } catch (error) {
      console.error('❌ Error sending via SMTP:', error);
      return {
        success: false,
        error: error.message || 'SMTP_ERROR'
      };
    }
  }

  /**
   * Send email using Supabase Edge Function with SMTP fallback
   * @private
   * @param {Object} emailData - Email data
   * @returns {Promise<Object>} Send result
   */
  async _sendEmail(emailData) {
    try {
      // Validate email data
      this._validateEmailData(emailData);

      // First, try to use the Supabase Edge Function
      try {
        const { data, error } = await supabaseService.client.functions.invoke('send-email', {
          body: {
            to: emailData.to,
            subject: emailData.subject,
            html: emailData.html,
            text: emailData.text
          }
        });

        if (error) {
          console.warn('Supabase function error:', error);
          console.log('Falling back to SMTP for email delivery...');
          // Continue to SMTP fallback
        } else if (data && data.success) {
          return {
            success: true,
            messageId: data.messageId || `msg_${Date.now()}`
          };
        } else {
          console.warn('Supabase function returned:', data);
          console.log('Falling back to SMTP for email delivery...');
          // Continue to SMTP fallback
        }
      } catch (supabaseError) {
        console.warn('Supabase function unavailable, using SMTP fallback:', supabaseError.message);
      }

      // Fallback: Use SMTP directly
      const smtpResult = await smtpEmailService.sendEmail(emailData);
      
      if (smtpResult.success) {
        return {
          success: true,
          messageId: smtpResult.messageId || `smtp_msg_${Date.now()}`
        };
      } else {
        throw new Error(`SMTP Error: ${smtpResult.error}`);
      }

    } catch (error) {
      console.error('Error in _sendEmail:', error);
      throw error;
    }
  }

  /**
   * Validate email data before sending
   * @private
   * @param {Object} emailData - Email data to validate
   */
  _validateEmailData(emailData) {
    // Sanitize email content first
    if (emailData.to) {
      emailData.to = emailData.to.toLowerCase().trim();
    }
    if (emailData.subject) {
      emailData.subject = emailData.subject.trim();
    }

    if (!emailData.to || !this._isValidEmail(emailData.to)) {
      throw new Error('Invalid recipient email address');
    }

    if (!emailData.subject || emailData.subject.length === 0) {
      throw new Error('Email subject is required');
    }

    if (!emailData.html && !emailData.text) {
      throw new Error('Email content (HTML or text) is required');
    }
  }

  /**
   * Validate email address format
   * @private
   * @param {string} email - Email address to validate
   * @returns {boolean} Is valid email
   */
  _isValidEmail(email) {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
  }

  /**
   * Generate user confirmation email content
   * @private
   * @param {string} userName - User's name
   * @param {string} walletAddress - User's wallet address
   * @returns {Object} Email content with HTML and text versions
   */
  _generateUserConfirmationEmail(userName, walletAddress) {
    const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Welcome to $PGC Whitelist</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f8f9fa;
        }
        .container {
            background: white;
            border-radius: 12px;
            padding: 40px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
        }
        .logo {
            width: 80px;
            height: 80px;
            margin: 0 auto 20px;
            border-radius: 50%;
            background: linear-gradient(135deg, #0a4b2a 0%, #1abc9c 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 32px;
            color: white;
            font-weight: bold;
        }
        .title {
            color: #0a4b2a;
            font-size: 28px;
            font-weight: bold;
            margin: 0;
            background: linear-gradient(135deg, #0a4b2a, #1abc9c);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
        .subtitle {
            color: #666;
            font-size: 16px;
            margin: 10px 0 0;
        }
        .content {
            margin: 30px 0;
        }
        .highlight-box {
            background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
            border: 2px solid #1abc9c;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
        }
        .wallet-info {
            background: #f8f9fa;
            border-radius: 8px;
            padding: 15px;
            margin: 15px 0;
            font-family: 'Courier New', monospace;
            word-break: break-all;
        }
        .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #eee;
            text-align: center;
            color: #666;
            font-size: 14px;
        }
        .social-links {
            margin: 20px 0;
        }
        .social-links a {
            color: #1abc9c;
            text-decoration: none;
            margin: 0 10px;
        }
        .warning {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 6px;
            padding: 15px;
            margin: 20px 0;
            color: #856404;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="logo">$PGC</div>
            <h1 class="title">Welcome to the $PGC Whitelist! 🚀</h1>
            <p class="subtitle">Your registration has been confirmed</p>
        </div>

        <div class="content">
            <p>Dear <strong>${this._sanitizeHtml(userName)}</strong>,</p>
            
            <div class="highlight-box">
                <p><strong>🎉 Congratulations!</strong> You have been successfully added to the $PGC Token whitelist for our limited-time airdrop.</p>
            </div>

            <p>Here are your registration details:</p>
            
            <div class="wallet-info">
                <strong>Wallet Address:</strong><br>
                ${this._sanitizeHtml(walletAddress)}
            </div>

            <div class="warning">
                <strong>⚠️ Important:</strong> Please ensure you have access to the wallet address above. The $PGC tokens will be sent directly to this address during the airdrop distribution.
            </div>

            <h3>What's Next?</h3>
            <ul>
                <li>Keep this email for your records</li>
                <li>Follow our social media channels for airdrop updates</li>
                <li>Join our community to stay informed about $PGC developments</li>
                <li>Ensure your wallet is ready to receive BEP-20 tokens</li>
            </ul>

            <h3>About $PGC Token</h3>
            <p>$PGC is building a sustainable future by turning plastic waste into clean energy. By participating in our whitelist, you're joining a community dedicated to environmental innovation and blockchain technology.</p>
        </div>

        <div class="footer">
            <div class="social-links">
                <a href="https://petgascoin.com">🌐 Website</a>
                <a href="mailto:${API_CONFIG.adminEmail}">📧 Support</a>
            </div>
            <p>This email was sent to confirm your $PGC whitelist registration.<br>
            If you didn't register for this whitelist, please contact our support team.</p>
            <p><small>© 2024 PetGasCoin. All rights reserved.</small></p>
        </div>
    </div>
</body>
</html>`;

    const text = `
Welcome to the $PGC Whitelist! 🚀

Dear ${userName},

Congratulations! You have been successfully added to the $PGC Token whitelist for our limited-time airdrop.

Registration Details:
- Name: ${userName}
- Wallet Address: ${walletAddress}

IMPORTANT: Please ensure you have access to the wallet address above. The $PGC tokens will be sent directly to this address during the airdrop distribution.

What's Next?
- Keep this email for your records
- Follow our social media channels for airdrop updates
- Join our community to stay informed about $PGC developments
- Ensure your wallet is ready to receive BEP-20 tokens

About $PGC Token:
$PGC is building a sustainable future by turning plastic waste into clean energy. By participating in our whitelist, you're joining a community dedicated to environmental innovation and blockchain technology.

Visit us: https://petgascoin.com
Support: ${API_CONFIG.adminEmail}

This email was sent to confirm your $PGC whitelist registration.
If you didn't register for this whitelist, please contact our support team.

© 2024 PetGasCoin. All rights reserved.
`;

    return { html, text };
  }

  /**
   * Generate admin notification email content
   * @private
   * @param {Object} entryData - Whitelist entry data
   * @returns {Object} Email content with HTML and text versions
   */
  _generateAdminNotificationEmail(entryData) {
    const registrationTime = new Date(entryData.createdAt).toLocaleString();
    
    const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>New $PGC Whitelist Registration</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f8f9fa;
        }
        .container {
            background: white;
            border-radius: 12px;
            padding: 30px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .header {
            background: linear-gradient(135deg, #0a4b2a, #1abc9c);
            color: white;
            padding: 20px;
            border-radius: 8px;
            text-align: center;
            margin-bottom: 30px;
        }
        .title {
            margin: 0;
            font-size: 24px;
            font-weight: bold;
        }
        .info-grid {
            display: grid;
            gap: 15px;
            margin: 20px 0;
        }
        .info-item {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            border-left: 4px solid #1abc9c;
        }
        .info-label {
            font-weight: bold;
            color: #0a4b2a;
            margin-bottom: 5px;
        }
        .info-value {
            font-family: 'Courier New', monospace;
            word-break: break-all;
        }
        .stats {
            background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
            border: 1px solid #1abc9c;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
            text-align: center;
        }
        .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #eee;
            color: #666;
            font-size: 14px;
            text-align: center;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1 class="title">🔔 New Whitelist Registration</h1>
            <p>A new user has joined the $PGC whitelist</p>
        </div>

        <div class="info-grid">
            <div class="info-item">
                <div class="info-label">👤 Name</div>
                <div class="info-value">${this._sanitizeHtml(entryData.name)}</div>
            </div>
            
            <div class="info-item">
                <div class="info-label">📧 Email</div>
                <div class="info-value">${this._sanitizeHtml(entryData.email)}</div>
            </div>
            
            <div class="info-item">
                <div class="info-label">💰 Wallet Address</div>
                <div class="info-value">${this._sanitizeHtml(entryData.walletAddress)}</div>
            </div>
            
            <div class="info-item">
                <div class="info-label">⏰ Registration Time</div>
                <div class="info-value">${registrationTime}</div>
            </div>
            
            ${entryData.ipAddress ? `
            <div class="info-item">
                <div class="info-label">🌐 IP Address</div>
                <div class="info-value">${this._sanitizeHtml(entryData.ipAddress)}</div>
            </div>
            ` : ''}
        </div>

        <div class="stats">
            <p><strong>Entry ID:</strong> ${this._sanitizeHtml(entryData.id)}</p>
            <p><small>This registration has been automatically processed and logged in the database.</small></p>
        </div>

        <div class="footer">
            <p>This is an automated notification from the $PGC Whitelist system.</p>
            <p><small>© 2024 PetGasCoin Admin System</small></p>
        </div>
    </div>
</body>
</html>`;

    const text = `
New $PGC Whitelist Registration

A new user has joined the $PGC whitelist:

Registration Details:
- Name: ${entryData.name}
- Email: ${entryData.email}
- Wallet Address: ${entryData.walletAddress}
- Registration Time: ${registrationTime}
- Entry ID: ${entryData.id}
${entryData.ipAddress ? `- IP Address: ${entryData.ipAddress}` : ''}

This registration has been automatically processed and logged in the database.

This is an automated notification from the $PGC Whitelist system.
© 2024 PetGasCoin Admin System
`;

    return { html, text };
  }

  /**
   * Sanitize HTML content to prevent XSS
   * @private
   * @param {string} text - Text to sanitize
   * @returns {string} Sanitized text
   */
  _sanitizeHtml(text) {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  /**
   * Send email verification request
   * @param {Object} entryData - Whitelist entry data
   * @returns {Promise<Object>} Result object
   */
  async sendVerificationRequest(entryData) {
    const emailType = 'email_verification';
    
    try {
      // Log email attempt as pending
      await supabaseService.logEmailAttempt({
        whitelistEntryId: entryData.id,
        emailType,
        recipientEmail: entryData.email,
        status: 'pending'
      });

      // Generate email content
      const emailContent = this._generateVerificationEmail(entryData.name);
      
      // Send email with retry logic
      const result = await this._sendEmailWithRetry({
        to: entryData.email,
        subject: '🔐 Verify your $PGC Whitelist Registration',
        html: emailContent.html,
        text: emailContent.text
      });

      // Log successful email
      await supabaseService.logEmailAttempt({
        whitelistEntryId: entryData.id,
        emailType,
        recipientEmail: entryData.email,
        status: 'sent',
        emailProviderId: result.messageId
      });

      return {
        success: true,
        message: 'Verification email sent successfully'
      };

    } catch (error) {
      console.error('Error sending verification email:', error);

      // Log failed email attempt
      try {
        await supabaseService.logEmailAttempt({
          whitelistEntryId: entryData.id,
          emailType,
          recipientEmail: entryData.email,
          status: 'failed',
          errorMessage: error.message
        });
      } catch (logError) {
        console.error('Error logging failed verification email attempt:', logError);
      }

      return {
        success: false,
        error: 'EMAIL_SEND_ERROR',
        message: 'Failed to send verification email'
      };
    }
  }

  /**
   * Generate verification email content
   * @private
   * @param {string} userName - User's name
   * @returns {Object} Email content with HTML and text versions
   */
  _generateVerificationEmail(userName) {
    const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Verify your $PGC Registration</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f8f9fa;
        }
        .container {
            background: white;
            border-radius: 12px;
            padding: 40px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
        }
        .logo {
            width: 80px;
            height: 80px;
            margin: 0 auto 20px;
            border-radius: 50%;
            background: linear-gradient(135deg, #0a4b2a 0%, #1abc9c 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 32px;
            color: white;
            font-weight: bold;
        }
        .title {
            color: #0a4b2a;
            font-size: 28px;
            font-weight: bold;
            margin: 0;
            background: linear-gradient(135deg, #0a4b2a, #1abc9c);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
        .subtitle {
            color: #666;
            font-size: 16px;
            margin: 10px 0 0;
        }
        .content {
            margin: 30px 0;
        }
        .highlight-box {
            background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
            border: 2px solid #1abc9c;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
            text-align: center;
        }
        .verification-instructions {
            text-align: center;
            margin: 30px 0;
        }
        .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #eee;
            text-align: center;
            color: #666;
            font-size: 14px;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="logo">$PGC</div>
            <h1 class="title">Verify Your Email 🔐</h1>
            <p class="subtitle">Please verify your registration to complete the process</p>
        </div>

        <div class="content">
            <p>Dear <strong>${this._sanitizeHtml(userName)}</strong>,</p>
            
            <p>Thank you for registering for the $PGC Token whitelist! We need to verify your email address to complete your registration.</p>
            
            <div class="highlight-box">
                <p><strong>Important:</strong> Please check your inbox for a verification message from us with a code to complete your registration.</p>
            </div>

            <div class="verification-instructions">
                <h3>How to Verify:</h3>
                <ol>
                    <li>Check your email inbox (and spam folder)</li>
                    <li>Find the email with the verification code</li>
                    <li>Enter the code on the whitelist form to complete verification</li>
                </ol>
            </div>

            <p>If you have any questions or need assistance, please contact our support team.</p>
        </div>

        <div class="footer">
            <p>This email was sent to verify your $PGC whitelist registration.<br>
            If you didn't register for this whitelist, please contact our support team.</p>
            <p><small>© 2024 PetGasCoin. All rights reserved.</small></p>
        </div>
    </div>
</body>
</html>`;

    const text = `
Verify Your $PGC Registration 🔐

Dear ${userName},

Thank you for registering for the $PGC Token whitelist! We need to verify your email address to complete your registration.

Please check your inbox for a verification message from us with a code to complete your registration.

How to Verify:
1. Check your email inbox (and spam folder)
2. Find the email with the verification code
3. Enter the code on the whitelist form to complete verification

If you have any questions or need assistance, please contact our support team at ${API_CONFIG.adminEmail}.

This email was sent to verify your $PGC whitelist registration.
If you didn't register for this whitelist, please contact our support team.

© 2024 PetGasCoin. All rights reserved.
`;

    return { html, text };
  }

  /**
   * Get email logs for a whitelist entry
   * @param {string} entryId - Whitelist entry ID
   * @returns {Promise<Array>} Array of email logs
   */
  async getEmailLogs(entryId) {
    try {
      return await supabaseService.getEmailLogs(entryId);
    } catch (error) {
      console.error('Error getting email logs:', error);
      return [];
    }
  }

  /**
   * Test email service connectivity
   * @returns {Promise<boolean>} Service status
   */
  async testEmailService() {
    try {
      // Test by calling the edge function with a test payload
      const { data, error } = await supabaseService.client.functions.invoke('send-email', {
        body: { test: true }
      });

      if (!error && data?.available) {
        return true;
      }
      
      // If Supabase function is not available, test SMTP connection
      console.log('Supabase email function not available, testing SMTP connection...');
      const smtpAvailable = await smtpEmailService.testConnection();
      return smtpAvailable;
    } catch (error) {
      console.error('Email service test failed:', error);
      
      // Test SMTP as fallback
      try {
        const smtpAvailable = await smtpEmailService.testConnection();
        return smtpAvailable;
      } catch (smtpError) {
        console.error('SMTP test also failed:', smtpError);
        return false;
      }
    }
  }
}

// Export singleton instance
export const emailService = new EmailService();

// Export default
export default emailService;